import $ from "jquery";

import {
  registerAiCapability,
  setAiCurrentPage,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";
import {
  getAiOmnibarMode,
  setAiOmnibarMode,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-omnibar.store";
import { isAiSearchEnabled } from "@medflyt/webapp-react/src/modules/betaFeatures/betaFeatures.store";

/* @ngInject */
export function globalSearch($rootScope, $state, DatabaseApi, Storage, $uibModal, $timeout, $transitions) {
    return {
        restrict: 'E',
        templateUrl: 'admin/views/global-search.html',
        link: function ($scope, elem, attr) {
            $scope.searchVal = '';
            $scope.results = {
                patients: [],
                caregivers: [],
                visits: [],
                invoices: []
            };
            $scope.resultsPreview = [];
            $scope.isSearching = false;
            $scope.searchDisplayFieldSelection = { val: "" };
            $scope.patientDisplayFieldsOptions = [
                "id",
                "first_name",
                "middle_name",
                "last_name",
                "address",
                "phone_numbers",
                "ssn",
                "member_id",
                "medicaid_number",
                "medicare_number",
                "office"
            ];
            $scope.caregiverDisplayFieldsOptions = [
                "id",
                "first_name",
                "middle_name",
                "last_name",
                "address",
                "ssn",
                "phone_numbers",
                "offices"
            ];
            $scope.defaultSearchFilters = {
                filters: {
                    patient: {
                        id: true,
                        first_name: true,
                        middle_name: true,
                        last_name: true,
                        address: true,
                        phone_numbers: true,
                        ssn: true,
                        member_id: true,
                        medicaid_number: true,
                        medicare_number: true,
                    },
                    caregiver: {
                        id: true,
                        first_name: true,
                        middle_name: true,
                        last_name: true,
                        address: true,
                        phone_numbers: true,
                        ssn: true,
                    },
                },
                displayFields: {
                    patient: ["first_name", "last_name", "office"],
                    caregiver: ["first_name", "last_name", "offices"],
                },
        };
        $scope.searchFilters = angular.copy($scope.defaultSearchFilters);

        const filtersFromStorage = Storage.getObject("searchFiltersSettings");
        if (Object.keys(filtersFromStorage).length) {
            $scope.searchFilters = JSON.parse(filtersFromStorage);
        }

        $scope.$watch(
            "searchFilters",
            function () {
                if ($scope.searchFilters) {
                    Storage.setObject("searchFiltersSettings", JSON.stringify($scope.searchFilters));
                }
            },
            true
        );

        $scope.resetAllSearchFilters = () => {
            $scope.searchFilters = angular.copy($scope.defaultSearchFilters);
            Storage.delete("searchFiltersSettings");
        };

        $scope.displayFieldSelected = (filterType) => {
            if ($scope.searchDisplayFieldSelection.val !== "") {
                $scope.searchFilters.displayFields[filterType].push($scope.searchDisplayFieldSelection.val);
                $scope.searchDisplayFieldSelection.val = "";
            }
        };

        $scope.removeDisplayField = (filterType, displayField) => {
            const filteredDisplayFields = $scope.searchFilters.displayFields[filterType].filter((field) => field !== displayField);
            $scope.searchFilters.displayFields[filterType] = filteredDisplayFields;
        };

        // --- Search / AI mode -------------------------------------------------------
        // The two modes share one pill. The CSS in custom.css cross-fades them off the
        // `ai-mode` class, so switching is only a boolean here.

        // AI search is a beta feature, off unless someone turns it on: without it there is no AI
        // pill, no AI input and nothing asking components what they can do. Read once per link
        // rather than watched, like the other beta switches - it says "takes effect on the next
        // page load" and a bar that changed shape mid-session would be worse than one that waits.
        $scope.isAiSearchEnabled = isAiSearchEnabled();

        // Navigating re-links this directive, so the chosen mode is kept in the omnibar store
        // rather than in this scope - the bar stays on AI after the AI navigates somewhere.
        $scope.isAiMode = $scope.isAiSearchEnabled && getAiOmnibarMode() === "ai";

        // The AI settings modal is React, mounted by this template while the flag is on. Closing
        // comes back from React, which is outside a digest - hence the $timeout, the same as the
        // mode switch below and the beta features modal.
        $scope.isAiSettingsOpen = false;

        // The one settings button, in both modes. In search mode the popover beside it does the
        // work and this does nothing; in AI mode the popover is disabled and this opens the panel.
        $scope.openSearchSettings = function () {
            if ($scope.isAiMode) {
                $scope.isAiSettingsOpen = true;
            }
        };

        $scope.closeAiSettings = function () {
            $timeout(function () {
                $scope.isAiSettingsOpen = false;
            });
        };

        $scope.setAiMode = function () {
            if (!$scope.isAiSearchEnabled) {
                return;
            }

            setAiOmnibarMode("ai");
            $timeout(function () {
                $scope.isAiMode = true;
                $scope.resetSearch();
            });
        };

        // Also called from React (the omnibar's onExit), which is outside a digest -
        // hence the $timeout on both paths.
        $scope.setSearchMode = function () {
            setAiOmnibarMode("search");
            $timeout(function () {
                $scope.isAiMode = false;
                const input = document.getElementById("global-search");
                if (input !== null) {
                    input.focus();
                }
            });
        };

        // The search bar tells the AI about itself, like every other component does.
        const releaseSearchCapability = registerAiCapability(
            {
                id: "search.entities",
                title: "Search patients, caregivers, visits and claims",
                effect: "prefill",
                description:
                    "Searches the agency for patients, caregivers, visits and payment claims, and lists what it found so the user can pick one. Use this when the user wants to find or look something up rather than go somewhere.",
                location: "the search bar in the top navigation, on every page",
                params: [
                    {
                        name: "text",
                        type: "text",
                        required: true,
                        description:
                            "What to search for: a name, an id, an address, a phone number, an SSN or a member id.",
                    },
                ],
                examples: ["find John Smith", "look up member id 12345"],
            },
            function (args) {
                const term = String(args.text);

                // Runs the same search the box runs, but hands the hits back as results the user
                // can act on, so a search does not throw them out of AI mode to click something.
                return runSearchForAi(term).then(function (rows) {
                    if (rows.length === 0) {
                        return { text: 'Nothing found for "' + term + '"' };
                    }

                    return {
                        text: 'Found ' + rows.length + ' for "' + term + '"',
                        results: rows,
                    };
                });
            }
        );

        /**
         * The agency search, mapped into rows the omnibar can execute. Each row names the
         * capability that opens that kind of record, so clicking one goes through the registry and
         * the write guard exactly like a step the model planned.
         */
        function runSearchForAi(term) {
            // Not the box's own filters: those enable the id fields, which are numeric in the
            // search index, and a multi_match mixing them with a name throws and fails every shard
            // - which is why typing a name in the search box finds nothing. Ask for the id fields
            // only when the term could be one.
            const isNumeric = /^\d+$/.test(term);
            const filters = isNumeric
                ? "&patient_id=true&caregiver_id=true"
                : "&patient_first_name=true&patient_middle_name=true&patient_last_name=true" +
                  "&caregiver_first_name=true&caregiver_middle_name=true&caregiver_last_name=true";
            const includeInvoices = $scope.showBillingFeature ? "&include_invoices=true" : "";
            const url =
                "agencies/" + $rootScope.agencyId + "/search?text=" + encodeURIComponent(term) +
                filters + includeInvoices;

            return DatabaseApi.get(url).then(function (res) {
                const data = res.data;
                const rows = [];

                (data.patients ?? []).slice(0, 6).forEach(function (patient) {
                    rows.push({
                        label: getResultDisplayValue("Patients", patient),
                        detail: "Patient",
                        capabilityId: "patient.openModal",
                        args: { patient: patient.id },
                    });
                });

                (data.caregivers ?? []).slice(0, 6).forEach(function (caregiver) {
                    rows.push({
                        label: getResultDisplayValue("Caregivers", caregiver),
                        detail: "Caregiver",
                        capabilityId: "caregiver.openModal",
                        args: { caregiver: caregiver.id },
                    });
                });

                (data.invoices ?? []).slice(0, 4).forEach(function (invoice) {
                    rows.push({
                        label: "Claim " + invoice.displayId,
                        detail: "Payment claim",
                        capabilityId: "invoice.open",
                        args: { invoice: invoice.id },
                    });
                });

                (data.visits ?? []).slice(0, 4).forEach(function (visit) {
                    rows.push({
                        label: "Visit " + visit.id,
                        detail: "Visit",
                        capabilityId: "visitBroadcast.open",
                        args: { broadcast: visit.id },
                    });
                });

                return rows;
            });
        }

        // Where the user is, so the AI can tell "this page" from a page it has to open.
        setAiCurrentPage($state.current.name ?? null);

        const releaseStateListener = $transitions.onSuccess({}, function (transition) {
            setAiCurrentPage(transition.to().name ?? null);
        });

        $scope.$on("$destroy", function () {
            releaseSearchCapability();
            releaseStateListener();
        });

        $scope.navbarSearchFocus = function () {
            $scope.isNavbarSearchFocused = true;
        };

        $scope.navbarSearchBlur = function () {
            $scope.isNavbarSearchFocused = false;
        };

        $scope.selectedIndex = -1;
        $scope.resultsPreviewAmount = 7;

        $scope.searchKeyUp = function (event) {
            var resultsAmount = $scope.resultsPreview.length;
            switch (event.key) {
                case 'Enter': {
                    if ($scope.selectedIndex === -1 ||
                        resultsAmount < $scope.resultsPreviewAmount && $scope.selectedIndex === resultsAmount ||
                        resultsAmount >= $scope.resultsPreviewAmount && $scope.selectedIndex === $scope.resultsPreviewAmount) {
                        $scope.searchFullResults($scope.searchVal);
                    } else {
                        $scope.resultPreviewClick($scope.resultsPreview[$scope.selectedIndex]);
                    }

                    break;
                }
                case 'Escape': {
                    $scope.searchVal = '';
                    event.target.blur();
                    $scope.selectedIndex = -1;
                    break;
                }
                case 'ArrowDown': {
                    if ($scope.selectedIndex < $scope.resultsPreviewAmount && $scope.selectedIndex < resultsAmount) {
                        $scope.selectedIndex++;
                    }
                    break;
                }
                case 'ArrowUp': {
                    if ($scope.selectedIndex > -1) {
                        $scope.selectedIndex--;
                    }
                    break;
                }
                default: {
                    $scope.selectedIndex = -1;
                    break;
                }
            }
        };

        $scope.searchChange = function () {
            if ($scope.searchVal.length < 3) {
                $scope.resultsPreview = [];
            } else {
                search();
            }
        };
        $scope.getResultPerviewId = (resultPreview) => {
            switch (resultPreview.category) {
                case 'Patients':
                    return resultPreview.displayId;
                case 'Caregivers':
                    return resultPreview.displayId;
                case 'Visits':
                    return resultPreview.id;
                default:
                    return '';
            }
        };

        const getSearchQueryOptions = (filtersOptions) => {
            const result = [];
            Object.keys(filtersOptions).map(option => {
                const filterOption = filtersOptions[option];
                Object.keys(filterOption).map(filter => {
                    const filterValue = filterOption[filter];
                    if (filterValue) {
                        result.push(`${option}_${filter}=${filterValue}`);
                    }
                })
            });
            if (result.length === 0) {
                return "";
            }
            return "&" + result.join("&")
        }

        const search = function (fullSearch = false) {
            const searchText = $scope.searchVal;
            const searchQueryOptions = getSearchQueryOptions($scope.searchFilters.filters);
            const includeInvoices = $scope.showBillingFeature ? "&include_invoices=true" : "";
            const searchAll = fullSearch ? "&full_search=true" : "";
            $scope.isSearching = true;
            const url = `agencies/${$rootScope.agencyId}/search?text=${searchText}${searchQueryOptions}${includeInvoices}${searchAll}`;
            return DatabaseApi.get(url).then(function (res) {
                $scope.results = res.data;
                const preview = res.data.patients.map(function (p) {
                    p.category = 'Patients';
                    p.displayValue = getResultDisplayValue('Patients', p);

                    return p;
                }).concat(res.data.caregivers.map(function (c) {
                    c.category = 'Caregivers';
                    c.displayValue = getResultDisplayValue('Caregivers', c);
                    return c;
                })).concat(res.data.visits.map(function (v) {
                    v.category = 'Visits';
                    v.displayValue = v.id;
                    return v;
                })).concat(res.data.invoices.map(function (i) {
                    i.category = 'Payment claim';
                    i.displayValue = i.displayId;
                    return i;
                }));;
                $scope.resultsPreview = preview;
                $scope.isSearching = false;

                return {
                    results: res.data,
                    searchText: searchText,
                    preview: preview
                }
            }, function (err) {
                $scope.isSearching = false;
            });
        };

        const getResultMiddleName = function (result) {
            return result.middleName ?
                result.firstName + ' ' + result.middleName + ' ' + result.lastName :
                result.firstName + ' ' + result.lastName;
        };

        const getResultDisplayValue = (resultType, resultProfile) => {
            switch (resultType) {
                case "Caregivers":
                    const caregiverFields = $scope.searchFilters.displayFields.caregiver;
                    if (caregiverFields.length) {
                        return (caregiverFields.map((field) => {
                            switch (field) {
                                case "first_name":
                                    return resultProfile.firstName;
                                case "middle_name":
                                    return resultProfile.middleName;
                                case "last_name":
                                    return resultProfile.lastName;
                                case "phone_numbers":
                                    return resultProfile.phoneNumbers;
                                case "id":
                                    return resultProfile.displayId;
                                case "offices":
                                    return `(${resultProfile.officeNames.join(", ")})`;
                                case "address":
                                case "ssn":
                                default:
                                    return resultProfile[field];
                            }
                        })
                        .filter((field) => Boolean(field))
                        .join(" - ")
                    );
                    } else {
                        return ((resultProfile.displayId ? resultProfile.displayId + " - " : "") + getResultMiddleName(resultProfile) + ` (${resultProfile.officeNames.join(", ")})`);
                    }
                case "Patients":
                    const patientFields = $scope.searchFilters.displayFields.patient;
                    if (patientFields.length) {
                        return (patientFields.map((field) => {
                            switch (field) {
                                case "first_name":
                                    return resultProfile.firstName;
                                case "middle_name":
                                    return resultProfile.middleName;
                                case "last_name":
                                    return resultProfile.lastName;
                                case "phone_numbers":
                                    return resultProfile.phoneNumbers;
                                case "id":
                                    return resultProfile.displayId;
                                case "member_id":
                                    return resultProfile.memberId;
                                case "medicaid_number":
                                    return resultProfile.medicaIDNumber;
                                case "medicare_number":
                                    return resultProfile.medicareNumber;
                                case "office":
                                    return `(${resultProfile.officeName})`;
                                case "address":
                                case "ssn":
                                default:
                                    return resultProfile[field];
                            }
                        })
                        .filter((field) => Boolean(field))
                        .join(" - "));
                    } else {
                        return ((resultProfile.displayId ? resultProfile.displayId + " - " : "") + getResultMiddleName(resultProfile) + ` (${resultProfile.officeName})`);
                    }
                default:
                    return "";
            }
        };

        $scope.searchFullResults = function () {
            if (!$scope.isFullSearching) {
                $scope.isFullSearching = true;
                search(true).then((res) => {
                    $state.go('app.globalSearch', {
                        searchText: res.searchText,
                        results: res.results,
                        resultsPreview: res.preview
                    });
                    $scope.resetSearch();
                }).finally(() => {
                    $scope.isFullSearching = false;
                });
            }
        }

        $scope.resetSearch = function () {
            $scope.searchVal = '';
            $scope.resultsPreview = [];
            $scope.results = {
                patients: [],
                caregivers: [],
                visits: [],
                invoices: []
            };
        };

        $scope.getPermissionKeyByCategory = (category) => {
            switch (category) {
                case 'Patients':
                    return 'view_patient_search_result';
                case 'Caregivers':
                    return 'view_caregiver_search_result';
                case 'Visits':
                case 'Payment claim':
                default:
                    return '';
            }
        };

        $scope.getIconSrcByCategory = function (category) {
            switch (category) {
                case 'Patients':
                    return 'admin/images/icons/patient.svg';
                case 'Caregivers':
                    return 'admin/images/icons/caregiver.svg';
                case 'Visits':
                    return 'admin/images/icons/visit.svg';
                case 'Payment claim':
                    return 'admin/images/icons/payment-claim.svg';
                default: return '';
            }
        };

        $scope.resultPreviewClick = function (resultPreview) {
            // $scope.resetSearch();
            switch (resultPreview.category) {
                case 'Patients': {
                    var forceModal = true;
                    $rootScope.openPatientModal(resultPreview.id, forceModal);
                    break;
                }
                case 'Caregivers': {
                    var forceModal = true;
                    $rootScope.openCaregiverModal(
                        resultPreview.id,
                        undefined,
                        forceModal
                    );
                    break;
                }
                case 'Visits': {
                    var url = 'agencies/' + $rootScope.agencyId +
                        '/coordinator/' + $rootScope.agencyMemberId +
                        '/visits/' + resultPreview.id;
                    DatabaseApi.get(url).then(function (res) {
                        const visit = res.data;
                        $rootScope.openVisitModal({ visit });
                    }, function (err) {

                    });
                    break;
                }
                case 'Payment claim': {
                    var url = 'agencies/' + $rootScope.agencyId +
                        '/agency_members/' + $rootScope.agencyMemberId +
                        '/invoices/' + resultPreview.id;
                    DatabaseApi.get(url).then(function (res) {
                        $rootScope.openInvoiceModal({ invoiceId: resultPreview.id });
                    }, function (err) {

                    });
                    break;
                }
                default: break;
            }

            $scope.navbarSearchBlur();
        };

        $('#global-search').on({
            focus: function () {
                $('.navbar-search').addClass('global-search-focused');
        },

            blur: function () {
                $('.navbar-search').removeClass('global-search-focused');
            },
        });

        $scope.$on('showBillingFeature_updated', function () {
            if ($rootScope.showBillingFeature) {
                $scope.showBillingFeature = true;
                search();
            }
        });

        if (elem[0].firstElementChild !== null) {
            elem[0].firstElementChild.addEventListener('animationend', function () {
                if ($rootScope.isNavbarLoaded !== true) {
                    $rootScope.isNavbarLoaded = true;
                }
            });
            }
        }
    };
  };
